import { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Shield, 
  Heart, 
  Ambulance,
  CheckCircle,
  ArrowRight,
  Clock,
  MapPin,
  FileText,
  Stethoscope,
  Activity,
  Home,
  Calendar,
  Phone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { siteConfig } from '../../config/site';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Hero Section
function HeroSection() {
  return (
    <section className="pt-32 pb-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-emerald-400 font-medium mb-4">
            Our Services
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Comprehensive
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400"> Medical Solutions</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400 leading-relaxed">
            From large-scale event coverage to personalized home care, we provide professional 
            medical services tailored to your specific needs.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// Event Medical Coverage Section
function EventCoverageSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const eventTypes = [
    'Music Festivals & Concerts',
    'Sporting Events & Marathons',
    'Corporate Events & Conferences',
    'Private Parties & Weddings',
    'Film & TV Productions',
    'Trade Shows & Exhibitions'
  ];

  const services = [
    { icon: Ambulance, label: 'On-site Medical Station' },
    { icon: Users, label: 'Roaming Medical Teams' },
    { icon: Activity, label: 'ALS/BLS Coverage' },
    { icon: Stethoscope, label: 'Physician Standby' },
    { icon: FileText, label: 'Post-Event Reports' },
    { icon: Clock, label: '24/7 Availability' }
  ];

  const process = [
    { step: '01', title: 'Plan', description: 'We assess your event needs, venue, and expected attendance to create a custom medical plan.' },
    { step: '02', title: 'Deploy', description: 'Our certified team arrives fully equipped and positioned strategically throughout your venue.' },
    { step: '03', title: 'Report', description: 'After the event, receive a comprehensive medical incident report for your records.' }
  ];

  return (
    <section ref={ref} id="events" className="py-24 bg-slate-950 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {/* Header */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                <span>Event Medical Coverage</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Professional On-Site Medical Teams
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                From intimate gatherings to massive festivals, we provide scalable medical 
                coverage that keeps your attendees safe and your event running smoothly.
              </p>
              <Button 
                asChild
                size="lg" 
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
              >
                <Link to="/contact?type=event-quote" data-testid="event-quote-btn">
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
              {services.map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                  <item.icon className="w-6 h-6 text-emerald-400 mb-2" />
                  <span className="text-slate-200 text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Who it's for */}
          <motion.div variants={fadeInUp} className="mb-16">
            <h3 className="text-xl font-semibold text-white mb-6">Who We Serve</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventTypes.map((type, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/30">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-slate-300">{type}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Process */}
          <motion.div variants={fadeInUp}>
            <h3 className="text-xl font-semibold text-white mb-6">Our Process</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {process.map((item, idx) => (
                <div key={idx} className="relative">
                  <div className="text-6xl font-bold text-emerald-500/20 mb-2">{item.step}</div>
                  <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                  <p className="text-slate-400">{item.description}</p>
                  {idx < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-1/2 border-t border-dashed border-slate-700" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Home Consultations Section
function HomeConsultationsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Stethoscope,
      title: 'Comprehensive Assessment',
      description: 'Full medical evaluation in the comfort of your home.'
    },
    {
      icon: FileText,
      title: 'Treatment & Prescriptions',
      description: 'On-site treatment and electronic prescriptions as needed.'
    },
    {
      icon: Activity,
      title: 'Specialist Referrals',
      description: 'Coordinated referrals to specialists when necessary.'
    },
    {
      icon: Clock,
      title: 'Same-Day Availability',
      description: 'Urgent appointments available within hours.'
    }
  ];

  return (
    <section ref={ref} id="home-visits" className="py-24 bg-slate-900 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div variants={fadeInUp} className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-6">
                <Home className="w-4 h-4" />
                <span>Private Home Consultations</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Quality Care at Your Doorstep
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Skip the waiting room. Our licensed physicians come to you, providing personalized 
                medical care for acute illnesses, chronic conditions, and preventive health needs.
              </p>

              <div className="space-y-4 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{feature.title}</h4>
                      <p className="text-slate-400 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                asChild
                size="lg" 
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
              >
                <Link to="/contact?type=home-visit" data-testid="home-visit-btn">
                  Book a Home Visit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </motion.div>

            {/* Info Cards */}
            <motion.div variants={fadeInUp} className="order-1 lg:order-2 space-y-4">
              <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800">
                <Calendar className="w-8 h-8 text-cyan-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Operating Hours</h3>
                <p className="text-slate-400 text-sm mb-4">
                  {siteConfig.hours.display}<br />
                  <span className="text-cyan-400">{siteConfig.hours.note}</span>
                </p>
              </div>
              <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800">
                <MapPin className="w-8 h-8 text-cyan-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Service Area</h3>
                <p className="text-slate-400 text-sm">
                  Currently serving {siteConfig.address.short} and surrounding areas across Saint Lucia. 
                  Contact us for coverage outside our primary service zone.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="space-y-8"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl font-bold text-white">
            Ready to Get Started?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400">
            Contact us today for a free consultation and customized quote.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              size="lg" 
              className="w-full sm:w-auto px-8 py-6 text-base bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/25"
            >
              <Link to="/contact?type=event-quote" data-testid="services-cta-quote">
                Request Event Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto px-8 py-6 text-base border-slate-700 text-white hover:bg-white/10 rounded-full"
            >
              <Link to="/contact" data-testid="services-cta-contact">
                <Phone className="mr-2 w-5 h-5" />
                Contact Us
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <MarketingLayout>
      <HeroSection />
      <EventCoverageSection />
      <HomeConsultationsSection />
      <CTASection />
    </MarketingLayout>
  );
}
